import { createSelector } from 'reselect';
import moment from 'moment';

import { initialState } from './reducer';
import { formatDate } from '../../utils/globalHelpers';

/**
 * Direct selector to the Payoff state domain
 */
const selectPayoffDomain = (state) => state.payoff || initialState;

/**
 * Other specific selectors
 */

/**
 * @description Selects a specific error from the error object
 */
const selectPayoffError = (prop) => createSelector(
  makeSelectPayoff('errors'),
  (errorState) => errorState[prop],
);

/**
 * @description Selects a specific service error from the service error object
 */
const selectPayoffServiceError = (prop) => createSelector(
  selectPayoffError('serviceError'),
  (errorState) => errorState[prop],
);

const selectAvailableDates = () => createSelector(
  makeSelectPayoff('availablePayOffDates'),
  (dates) => (
    dates.reduce((acc, val) => {
      acc[moment(val).format('YYYY-MM-DD')] = { disabled: false };
      return acc;
    }, {})
  ),
);

const selectAvailablePayOffReasons = () => createSelector(
  makeSelectPayoff('availablePayOffReasons'),
  (availablePayOffReasons) => availablePayOffReasons,
);

/**
 * Default selector used by Payoff
 */
const makeSelectPayoff = (prop) => createSelector(
  selectPayoffDomain,
  (state) => state[prop],
);

/**
 * @description Selects payoff history data and formats the date
 */

const selectPayoffHistoryData = () => createSelector(
  makeSelectPayoff('payoffHistoryData'),
  (data) => {
    const formattedData = data.map(({ payOffDate, ...restProps }) => {
      const formattedDate = formatDate(payOffDate, 'UTC', 'll');
      return { payoffDate: formattedDate, ...restProps };
    });
    return formattedData;
  },
);

/**
 * @description Selects errors for screen reader
 */
const selectPayoffNotifications = () => createSelector(
  selectPayoffError('bannerError'),
  (bannerError) => (
    Object.values(bannerError).filter((error) => !!error)
  ),
);

/**
 * @description determines total amount of past payoff statements
 */

const selectPayoffHistoryCount = () => createSelector(
  makeSelectPayoff('payoffHistoryData'),
  (data) => data.length,
);

/**
 * @description selects specified loading state
 */

const selectPayoffLoading = (prop) => createSelector(
  makeSelectPayoff('loading'),
  (state) => state[prop],
);

const selectIsPayDownAvailable = () => createSelector(
  makeSelectPayoff('availablePayDownDates'),
  (dates) => !!dates.length,
);

const selectPayoffHomeButtonsView = () => createSelector(
  makeSelectPayoff('availablePayDownDates'),
  makeSelectPayoff('statement'),
  (paydownDates, statement) => {
    if (statement) return 'statement';
    if (paydownDates.length) return 'paydown';
    return 'payoff';
  },
);

const selectPayoffRequestBody = () => createSelector(
  makeSelectPayoff('availablePayDownDates'),
  makeSelectPayoff('requestType'),
  makeSelectPayoff('selectedDate'),
  makeSelectPayoff('selectedPayOffReason'),
  (paydownDates, requestType, date, payoffReason) => {
    if (requestType === 'paydown') {
      return {
        payoffDate: paydownDates[0],
        requestType: 'PayDown',
      };
    }
    return {
      payoffDate: date,
      payoffReason,
      requestType: 'PayOff',
    };
  },
);

export default makeSelectPayoff;
export {
  selectAvailableDates,
  selectAvailablePayOffReasons,
  selectIsPayDownAvailable,
  selectPayoffError,
  selectPayoffHistoryCount,
  selectPayoffHistoryData,
  selectPayoffHomeButtonsView,
  selectPayoffLoading,
  selectPayoffNotifications,
  selectPayoffRequestBody,
  selectPayoffServiceError,
};
